import {
  Flex,
  Card,
  formatNumber,
  CurrencyValue,
  LineChart,
  DisplaySwitch,
  ChartSkeleton
} from '@blockanalitica/ui';
import { useCallback, useMemo, useState } from 'react';
import { useApi } from '@/api';
import type {
  SimpleChartData,
  SimpleChartDataset
} from '@blockanalitica/ui/package/types/src/components/charts/SimpleChart';
import { ChartTimeFilter } from '@/components';
import { useSearchParams } from '@/hooks';
import { useChartFillDatasetsDates } from '@/hooks';

export default function HistoricGraphs({
  walletAddress,
  marketAddress,
  underlyingSymbol,
  network
}: {
  walletAddress: string;
  marketAddress: string;
  underlyingSymbol: string;
  network: string;
}) {
  const [chartDaysAgo, setChartDaysAgo] = useState('30');
  const [data, error] = useApi<SimpleChartData[]>({
    url: `/markets/${marketAddress}/wallets/${walletAddress}/historic/`,
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  const [tab, setTab] = useSearchParams('tab', 'supply');

  const displayOptions = useMemo(
    () => [
      { key: 'supply', value: 'Supply' },
      { key: 'borrow', value: 'Borrow' }
    ],
    []
  );

  const yScaleValueFormatter = useCallback((value: string | number) => {
    return `${formatNumber(Number(value))}`;
  }, []);

  const valueFormatter = useCallback(
    (value: string | number) => {
      return (
        <CurrencyValue
          value={value}
          variant="dynamic"
          options={{
            currencySize: '3',
            currency: {
              name: underlyingSymbol,
              address: marketAddress,
              network: network
            }
          }}
        />
      );
    },
    [underlyingSymbol, marketAddress, network]
  );

  const datasets: SimpleChartDataset[] = [
    {
      xField: 'date',
      yField: tab
    }
  ];
  const chartData = useChartFillDatasetsDates(data, datasets, chartDaysAgo);

  if (error) {
    throw error;
  }

  if (!data) {
    return <ChartSkeleton />;
  }

  if (data) {
    return (
      <Card gap="xs">
        <Flex
          justifyContent="space-between"
          alignItems={['center', 'flex-start']}
          flexDirection={['column', 'row']}
          gap={['xs', 'm']}>
          <DisplaySwitch
            data={displayOptions}
            activeOption={tab}
            onChange={setTab}
          />
          <ChartTimeFilter
            activeOption={chartDaysAgo}
            onChange={setChartDaysAgo}
          />
        </Flex>
        <LineChart
          key={tab}
          data={chartData}
          datasets={datasets}
          valueFormatter={valueFormatter}
          yScaleValueFormatter={yScaleValueFormatter}
          daysAgo={chartDaysAgo}
        />
      </Card>
    );
  }
}
